/**
 * Loop-based audio layer manager.
 * Plays looped sounds tied to scene ranges from story config.
 * Used for both atmosphere (atmo) and music tracks.
 * Uses scroll position to determine which track should be active,
 * cross-fading between tracks as the player scrolls.
 */

import { Howl, Howler } from 'howler'
import { assetUrl } from '../../utils/pathResolver'
import { isScrollSoundsSuppressed } from '../scenes/animations/scroll-sound'
import { unlockAudioContext } from './unlock-audio-context'

const FADE_DURATION = 2000

export interface LoopTrackTrigger {
  start: { element: number; viewport: number }
  end: { element: number; viewport: number }
}

export interface LoopTrackConfig {
  name: string
  /** [from, to] — each can be a string or string[] (array = choice scenes, any match counts) */
  playFromTo: [string | string[], string | string[]]
}

export interface LoopLayerConfig {
  trigger?: LoopTrackTrigger
  tracks: LoopTrackConfig[]
}

interface ActiveTrack {
  key: string
  howl: Howl
  soundId: number
}

export class AtmoManager {
  private tracks: Map<string, Howl> = new Map()
  private trackUrls: Map<Howl, string> = new Map()
  private configs: LoopTrackConfig[]
  private trigger: LoopTrackTrigger | null
  private storySceneIds: string[]
  private activeTrack: ActiveTrack | null = null
  private scrollHandler: (() => void) | null = null
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private gameScreen: HTMLElement | null = null
  private targetVolume: number = 1
  private label: string

  constructor(layerConfig: LoopLayerConfig, basePath: string, storySceneIds: string[] = []) {
    this.configs = layerConfig.tracks
    this.trigger = layerConfig.trigger ?? null
    this.storySceneIds = storySceneIds
    this.label = basePath.includes('music') ? 'music' : 'atmo'

    for (const cfg of this.configs) {
      const soundPath = assetUrl(`${basePath}/${cfg.name}`)
      const howl = new Howl({
        src: [soundPath],
        loop: true,
        volume: 0,
        preload: false,
        html5: false,
      })
      this.tracks.set(cfg.name, howl)
      this.trackUrls.set(howl, soundPath)
    }
  }

  private loadLowPriority(howl: Howl): void {
    if (howl.state() !== 'unloaded') return
    const url = this.trackUrls.get(howl)
    if (!url) { howl.load(); return }
    fetch(url, { priority: 'low' } as RequestInit)
      .catch(() => {})
      .finally(() => { if (howl.state() === 'unloaded') howl.load() })
  }

  init(gameScreen: HTMLElement): void {
    this.gameScreen = gameScreen

    this.scrollHandler = () => {
      this.update()
    }

    window.addEventListener('scroll', this.scrollHandler, { passive: true })
    this.pollTimer = setInterval(() => this.update(), 500)
    requestAnimationFrame(() => this.update())
  }

  /** Set target volume (0-1). Immediately adjusts any currently playing track. */
  setVolume(volume: number): void {
    this.targetVolume = Math.max(0, Math.min(1, volume))
    if (this.activeTrack) {
      this.activeTrack.howl.volume(this.targetVolume, this.activeTrack.soundId)
    }
  }

  private update(): void {
    if (!this.gameScreen) return

    if (isScrollSoundsSuppressed()) {
      if (this.activeTrack) {
        const prev = this.activeTrack
        this.activeTrack = null
        try { prev.howl.fade(this.targetVolume, 0, FADE_DURATION, prev.soundId) } catch { /* ignore */ }
        const { howl, soundId } = prev
        setTimeout(() => { try { howl.stop(soundId) } catch { /* ignore */ } }, FADE_DURATION)
      }
      return
    }

    const targetKey = this.getActiveTrackKey()
    const currentKey = this.activeTrack?.key ?? null

    if (targetKey === currentKey) return

    // Stop previous track
    if (this.activeTrack) {
      const prev = this.activeTrack
      this.activeTrack = null
      try {
        prev.howl.fade(this.targetVolume, 0, FADE_DURATION, prev.soundId)
      } catch { /* ignore */ }
      const { howl, soundId } = prev
      setTimeout(() => { try { howl.stop(soundId) } catch { /* ignore */ } }, FADE_DURATION)
    }

    // Start new track
    if (targetKey) {
      const howl = this.tracks.get(targetKey)
      if (!howl) return

      const capturedKey = targetKey
      const play = () => {
        if (this.activeTrack?.key === capturedKey) return
        if (this.activeTrack) {
          try { this.activeTrack.howl.stop(this.activeTrack.soundId) } catch { /* ignore */ }
          this.activeTrack = null
        }
        const soundId = howl.play() as unknown as number
        howl.volume(0, soundId)
        howl.fade(0, this.targetVolume, FADE_DURATION, soundId)
        this.activeTrack = { key: capturedKey, howl, soundId }
      }

      howl.once('playerror', () => {
        unlockAudioContext()
        const ctx = Howler.ctx as AudioContext | undefined
        if (ctx && ctx.state === 'suspended') {
          ctx.resume().then(() => { howl.once('unlock', play) })
        } else {
          howl.once('unlock', play)
        }
      })

      if (howl.state() === 'loaded') {
        play()
      } else if (howl.state() === 'loading') {
        howl.once('load', play)
      } else {
        this.loadLowPriority(howl)
        howl.once('load', play)
      }
    }
  }

  /**
   * Resolve a playFromTo endpoint spec to an index in the authoritative scene list.
   */
  private resolveIndex(spec: string | string[]): number {
    const list = this.storySceneIds
    if (typeof spec === 'string') return list.indexOf(spec)
    for (const id of spec) {
      const idx = list.indexOf(id)
      if (idx !== -1) return idx
    }
    return -1
  }

  /**
   * Resolve a playFromTo spec (string or string[]) to a DOM wrapper.
   * For choice scenes (array), returns the first one found in the DOM.
   */
  private resolveWrapper(spec: string | string[], wrapperMap: Map<string, Element>): Element | null {
    if (typeof spec === 'string') return wrapperMap.get(spec) ?? null
    for (const id of spec) {
      const w = wrapperMap.get(id)
      if (w) return w
    }
    return null
  }

  /**
   * Find which track should play.
   *
   * When a trigger is configured, each track's start condition is checked
   * against its FROM scene and end condition against its TO scene.
   * Otherwise falls back to: last wrapper whose top edge is above viewport center.
   */
  private getActiveTrackKey(): string | null {
    if (!this.gameScreen) return null
    if (this.storySceneIds.length === 0) return null

    const wrappers = Array.from(this.gameScreen.querySelectorAll('.scene-wrapper'))
    if (wrappers.length === 0) return null

    if (this.trigger) {
      return this.getActiveTrackKeyWithTrigger(wrappers)
    }
    return this.getActiveTrackKeyLegacy(wrappers)
  }

  /**
   * Determine if a missing wrapper's scene is above or below the current
   * viewport by comparing its story index to the rendered wrapper range.
   * Returns 'above' | 'below' | 'unknown'.
   */
  private inferMissingPosition(
    spec: string | string[],
    minRenderedIdx: number,
    maxRenderedIdx: number,
  ): 'above' | 'below' | 'unknown' {
    const idx = this.resolveIndex(spec)
    if (idx === -1) return 'unknown'
    if (idx < minRenderedIdx) return 'above'
    if (idx > maxRenderedIdx) return 'below'
    return 'unknown'
  }

  private getActiveTrackKeyWithTrigger(wrappers: Element[]): string | null {
    const vh = window.innerHeight
    const wrapperMap = new Map<string, Element>()
    let minRenderedIdx = Infinity
    let maxRenderedIdx = -1
    for (const w of wrappers) {
      const id = w.getAttribute('data-scene')
      if (id) {
        wrapperMap.set(id, w)
        const idx = this.storySceneIds.indexOf(id)
        if (idx !== -1) {
          if (idx < minRenderedIdx) minRenderedIdx = idx
          if (idx > maxRenderedIdx) maxRenderedIdx = idx
        }
      }
    }

    let result: string | null = null
    for (const cfg of this.configs) {
      const fromWrapper = this.resolveWrapper(cfg.playFromTo[0], wrapperMap)
      const toWrapper = this.resolveWrapper(cfg.playFromTo[1], wrapperMap)
      if (!fromWrapper && !toWrapper) continue

      let startMet: boolean
      if (fromWrapper) {
        const fromRect = fromWrapper.getBoundingClientRect()
        const startPt = fromRect.top + (this.trigger!.start.element / 100) * fromRect.height
        startMet = startPt <= vh * (this.trigger!.start.viewport / 100)
      } else {
        const pos = this.inferMissingPosition(cfg.playFromTo[0], minRenderedIdx, maxRenderedIdx)
        startMet = pos === 'above'
      }

      let endMet: boolean
      if (toWrapper) {
        const toRect = toWrapper.getBoundingClientRect()
        const endPt = toRect.top + (this.trigger!.end.element / 100) * toRect.height
        endMet = endPt >= vh * (this.trigger!.end.viewport / 100)
      } else {
        const pos = this.inferMissingPosition(cfg.playFromTo[1], minRenderedIdx, maxRenderedIdx)
        if (pos === 'below') {
          endMet = true
        } else if (pos === 'unknown') {
          // TO scene not in scene list — only play while FROM scene is still rendered
          endMet = fromWrapper !== null
        } else {
          endMet = false
        }
      }

      if (startMet && endMet) result = cfg.name
    }

    return result
  }

  private getActiveTrackKeyLegacy(wrappers: Element[]): string | null {
    const viewportCenter = window.innerHeight / 2
    let centerSceneId: string | null = null
    for (const w of wrappers) {
      if (w.getBoundingClientRect().top <= viewportCenter) {
        centerSceneId = w.getAttribute('data-scene')
      }
    }
    if (!centerSceneId) return null

    const centerIdx = this.storySceneIds.indexOf(centerSceneId)
    if (centerIdx === -1) return null

    for (const cfg of this.configs) {
      const fromIdx = this.resolveIndex(cfg.playFromTo[0])
      const toIdx = this.resolveIndex(cfg.playFromTo[1])
      if (fromIdx === -1 || toIdx === -1) continue
      if (centerIdx >= fromIdx && centerIdx <= toIdx) return cfg.name
    }
    return null
  }

  destroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }

    if (this.activeTrack) {
      this.activeTrack.howl.stop(this.activeTrack.soundId)
      this.activeTrack = null
    }

    for (const howl of this.tracks.values()) {
      howl.stop()
      howl.unload()
    }
    this.tracks.clear()
    this.gameScreen = null
  }
}
